<template>
  <div>
    <apex-chart-card
      v-if="hasData"
      chart-title="Histórico de faturamento dos produtos da campanha"
      :filter-by-date-period.sync="filterByDatePeriod"
      :config-update-options="configUpdateOptions"
      :chart-loading="$apollo.queries.generalMetricsKpiRevenueHistory.loading || $apollo.queries.metricsKpiRevenueHistory.loading || loading"
      :chart-data="metricsKpiRevenueHistory"
      :series-data="seriesData"
      :current-chart-data.sync="currentChartData"
      is-stacked
      :data-for-export-csv="dataForExportCsv"
    />
    <v-card-title v-if="!hasData" class="pt-5 pl-6">
      <v-container class="pa-0 pr-1" fluid fill-height>
        <h2 class="table-card-title">Histórico de faturamento dos produtos da campanha</h2>
      </v-container>
    </v-card-title>
    <empty-chart v-if="!hasData" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'

import ApexChartCard from '@/components/ApexChartCard.vue'
import { assertArray, localeString } from '@/config/helpers.js'
import { QUERY_GET_METRICS } from '@/modules/maria/campanha-da-maria/graphql'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'

export default {
  name: 'ProductBillingHistory',
  components: {
    ApexChartCard,
    EmptyChart,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    units: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    filterByDatePeriod: 'diario',
    locale: localeString,
    metricsKpiRevenueHistory: [],
    generalMetricsKpiRevenueHistory: [],
    currentChartData: [],
    products: [],
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    productIds() {
      if (!this.campaign.offer_sales || !this.campaign.offer_sales.length) return []

      return this.campaign.offer_sales.map((offer) => offer.sku)
    },
    hasData() {
      return (
        this.metricsKpiRevenueHistory.reduce((total, valor) => total + valor.campaignRevenues, 0) +
        this.metricsKpiRevenueHistory.reduce((total, valor) => total + valor.generalRevenues, 0)
      )
    },
    dataForExportCsv() {
      let headers = []
      let data = []
      let filename = 'historico_faturamento_campanha_maria'
      if (this.queryGroupFilterByDatePeriod === 'WEEK') {
        headers = ['Faturamento total', 'Faturamento das campanhas', 'Campanhas disparadas', 'Data início', 'Data final']
        data = assertArray(this.metricsKpiRevenueHistory).map((revenue) => {
          return [revenue.generalRevenues, revenue.campaignRevenues, revenue.totalSent, revenue.label[0], revenue.label[1]]
        })
      } else {
        headers = ['Faturamento total', 'Faturamento das campanhas', 'Campanhas disparadas', 'Data']
        data = assertArray(this.metricsKpiRevenueHistory).map((revenue) => {
          return [revenue.generalRevenues, revenue.campaignRevenues, revenue.totalSent, revenue.label[0]]
        })
      }
      return { headers, data, filename }
    },
    skipRevenueHistory() {
      return !this.campaign._id || this.$apollo.queries.generalMetricsKpiRevenueHistory.loading || !this.generalMetricsKpiRevenueHistory.length
    },
    defaultQueryVariables() {
      const variables = {
        initialDate: this.campaign.initial_date,
        finalDate: this.campaign.final_date,
        groupBy: this.queryGroupFilterByDatePeriod,
      }
      return variables
    },
    queryGroupFilterByDatePeriod() {
      switch (this.filterByDatePeriod) {
        case 'semanal':
          return 'WEEK'
        case 'diario':
          return 'DAY'
        default:
          return 'MONTH'
      }
    },
    configUpdateOptions() {
      return {
        tooltip: {
          custom: ({ series, dataPointIndex }) => {
            const { newChartLabels } = this.currentChartData
            let periodTitle = newChartLabels[dataPointIndex]
            const generalRevenues = series[0]
            const campaignRevenues = series[1]
            const revenueByPeriod = generalRevenues[dataPointIndex]
            const campaignRevenueByPeriod = campaignRevenues[dataPointIndex]
            const totalRevenueByPeriod = campaignRevenueByPeriod + revenueByPeriod
            const revenuePercentage = (campaignRevenueByPeriod * 100) / totalRevenueByPeriod || 0
            if (this.queryGroupFilterByDatePeriod === 'WEEK') periodTitle = `${periodTitle[0]} até ${periodTitle[1]}`
            return `
                  <v-card class="py-2">
                    <v-card-text>
                      <div class="px-4 tooltip-container">
                        <h2 class="ma-0 mb-2">${periodTitle}</h2>
                        <h3 class="pa-0 ma-0 mb-1">
                          <span class="product-billing-history-primary-color">R$${this.locale(totalRevenueByPeriod, 2)}</span>
                          Faturamento total
                        </h3>
                        <h3 class="pa-0 ma-0 mb-1">
                          <span class="product-billing-history-marca-1">R$${this.locale(campaignRevenueByPeriod, 2)}</span>
                          Faturamento na campanha
                        </h3>
                        <p class="ma-0 mb-1 product-billing-history-cinza-0">${this.locale(revenuePercentage, 2)}% do faturamento total</p>
                      </div>
                    </v-card-text>
                  </v-card>
                `
          },
        },
        legend: {
          horizontalAlign: 'left',
          markers: {
            width: 7,
            height: 7,
            radius: 5,
          },
          shape: 'circle',
          offsetX: 50,
        },
      }
    },
    seriesData() {
      return [
        {
          name: 'Faturamento total',
          chartDataPropertyName: 'generalRevenues',
        },
        {
          name: 'Faturamento das campanhas',
          chartDataPropertyName: 'campaignRevenues',
        },
      ]
    },
  },
  apollo: {
    generalMetricsKpiRevenueHistory: {
      query: QUERY_GET_METRICS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ metricsKpi }) {
        const { revenue } = metricsKpi
        return revenue.map((revenue) => {
          return {
            value: revenue?.value,
            label: [DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')],
          }
        })
      },
      variables() {
        return {
          initialDate: this.campaign.initial_date.slice(0, -2).concat('01'),
          finalDate: this.campaign.final_date,
          campaignIds: [],
          units: this.units,
          groupBy: this.queryGroupFilterByDatePeriod,
          productIds: this.productIds,
        }
      },
      skip() {
        return !this.defaultQueryVariables || !this.defaultQueryVariables.initialDate || !this.defaultQueryVariables.finalDate
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
    },
    metricsKpiRevenueHistory: {
      query: QUERY_GET_METRICS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ metricsKpi }) {
        const { total_coupons, revenue } = metricsKpi
        const generalRevenues = this.generalMetricsKpiRevenueHistory.slice(
          this.generalMetricsKpiRevenueHistory.findIndex((metric) => {
            return metric.label[0] === DateTime.fromFormat(revenue[0].date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          })
        )
        return revenue.map((revenue, index) => {
          return {
            campaignRevenues: revenue?.value,
            generalRevenues: this.isLessThanZero(generalRevenues[index]?.value - revenue?.value),
            totalSent: total_coupons[index]?.value,
            label:
              revenue.date.length === 1
                ? [DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')]
                : [
                    DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                    DateTime.fromFormat(revenue.date[1], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                  ],
          }
        })
      },
      variables() {
        return {
          initialDate: this.campaign.initial_date,
          finalDate: this.campaign.final_date,
          campaignIds: [this.campaign._id],
          units: this.units,
          groupBy: this.queryGroupFilterByDatePeriod,
          productIds: this.productIds,
        }
      },
      skip() {
        return this.skipRevenueHistory
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
    },
  },
  watch: {
    productIds: {
      deep: true,
      handler: function (newValue) {
        if (!this.skipRevenueHistory) {
          this.products = newValue
          const variables = {
            ...this.defaultQueryVariables,
            campaignIds: [this.campaign._id],
            productIds: this.products,
          }
          this.$apollo.queries.metricsKpiRevenueHistory.refetch({
            ...variables,
          })
        }
      },
    },
  },
  methods: {
    isLessThanZero(value) {
      if (value < 0) return 0
      return value
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .tooltip-container {
  & h3 {
    font-family: 'Lato', sans-serif;
    color: $cinza-d10;
    & span {
      font-weight: 700;
      color: $cinza-d10;
    }
  }
}

::v-deep .product-billing-history-marca-1 {
  color: $marca-1;
}
::v-deep .product-billing-history-primary-color {
  color: $marca-0;
}
::v-deep .product-billing-history-cinza-0 {
  color: $cinza-0;
}
</style>
